import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiX, FiChevronDown, FiChevronUp, FiAlertCircle } = FiIcons;

const severityStyle = {
  high: 'border-l-2 border-red-400 bg-red-50',
  medium: 'border-l-2 border-amber-400 bg-amber-50',
};

const AlertsPanel = ({ data }) => {
  const { learners, complianceRecords, phishingSimulations, assignments = [], auditLogs = [] } = data;
  const [dismissed, setDismissed] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const buildAlerts = () => {
    const generatedAlerts = [];
    
    complianceRecords.filter(r => r.status === 'Overdue').forEach(r => {
      const learner = learners.find(l => String(l.id) === String(r.user_id) || l.user_id === r.user_id);
      const name = learner ? `${learner.firstName} ${learner.lastName}` : 'Unknown Learner';
      generatedAlerts.push({ 
        id: `cr-${r.id}`, 
        severity: 'high', 
        message: `${name} — compliance overdue`, 
        sub: `Due: ${r.next_audit || 'Past due'}` 
      });
    });

    learners.forEach(l => {
      let score = 20; 
      const lLogs = auditLogs.filter(a => String(a.user_id) === String(l.id));
      const clicks = lLogs.filter(a => a.action === 'Phishing Click' || a.action === 'click').length;
      const submissions = lLogs.filter(a => a.action === 'Credential Submission' || a.action === 'submission').length;
      const reports = lLogs.filter(a => a.action === 'Phishing Report' || a.action === 'report').length;
      score += (clicks * 15) + (submissions * 25) - (reports * 5);
      
      const lAssignments = assignments.filter(a => String(a.employee_id) === String(l.id));
      score += lAssignments.filter(a => a.status === 'Overdue').length * 10;
      score = Math.max(0, Math.min(100, score));

      if (score >= 50) {
        generatedAlerts.push({ 
          id: `lr-${l.id}`, 
          severity: 'high', 
          message: `${l.firstName} ${l.lastName} — high phishing risk`, 
          sub: `Score: ${score}` 
        });
      }
    });

    phishingSimulations.forEach(s => {
      const clickRate = s.sent > 0 ? (s.clicked / s.sent) * 100 : 0;
      if (clickRate > 15) {
        generatedAlerts.push({ 
          id: `ps-${s.id}`, 
          severity: 'medium', 
          message: `"${s.name}" — high click rate`, 
          sub: `${clickRate.toFixed(1)}% clicked` 
        });
      }
    });

    return generatedAlerts;
  };

  const alerts = buildAlerts().filter(a => !dismissed.includes(a.id));

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} className="text-red-500 text-sm" />
          <span className="text-sm font-semibold text-slate-800">Alerts</span>
          {alerts.length > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{alerts.length}</span>
          )}
        </div>
        <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-slate-400 text-sm" />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="p-3 space-y-1.5 max-h-72 overflow-y-auto">
              {alerts.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-4">All clear — no active alerts</p>
              )}
              {alerts.map(a => (
                <div key={a.id} className={`flex items-start gap-2.5 p-3 rounded-lg ${severityStyle[a.severity]}`}>
                  <SafeIcon icon={FiAlertTriangle} className="text-xs flex-shrink-0 mt-0.5 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-800 leading-snug">{a.message}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{a.sub}</p>
                  </div>
                  <button onClick={() => setDismissed(d => [...d, a.id])} className="text-slate-300 hover:text-slate-500 flex-shrink-0">
                    <SafeIcon icon={FiX} className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AlertsPanel;